/**
 * Page-break engine for the spreadsheet document (A4), used by the print window.
 * Pure function: rows are never split; scale increases a row's effective footprint.
 *
 * `repeat_on_each_page` sections keep their place in document order on page 1
 * (so a ColumnHeader band does NOT jump above the document Header). On page 2+
 * those bands are prepended, then the remaining non-repeat flow continues.
 */

export const MM_TO_PX = 3.779527559;

export interface PageMarginsLike {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export interface PageSettingsLike {
  orientation: 'portrait' | 'landscape';
  margins_mm: PageMarginsLike;
  scale_pct: number;
}

export interface PaginateRowLike {
  index: number;
  height_px: number;
  cells: unknown[];
}

export interface PaginateSectionLike {
  name?: string;
  repeat_on_each_page: boolean;
  rows: PaginateRowLike[];
}

export interface SpreadsheetModelLike {
  sections: PaginateSectionLike[];
}

export interface PrintPage {
  rows: Array<{ sectionIndex: number; rowIndex: number }>;
}

const A4_MM = { long: 297, short: 210 };

export function pageContentHeightPx(s: PageSettingsLike): number {
  const pageMm = s.orientation === 'landscape' ? A4_MM.short : A4_MM.long;
  const contentMm = pageMm - (s.margins_mm.top + s.margins_mm.bottom);
  return Math.max(contentMm, 10) * MM_TO_PX;
}

type FlatRow = {
  sectionIndex: number;
  rowIndex: number;
  h: number;
  repeat: boolean;
};

export function paginate(model: SpreadsheetModelLike, s: PageSettingsLike): PrintPage[] {
  const scale = Math.max(s.scale_pct, 1) / 100;
  const contentH = pageContentHeightPx(s);

  const allRows: FlatRow[] = [];
  const repeatRows: FlatRow[] = [];
  model.sections.forEach((section, sectionIndex) => {
    section.rows.forEach((row, rowIndex) => {
      const item: FlatRow = {
        sectionIndex,
        rowIndex,
        h: row.height_px * scale,
        repeat: !!section.repeat_on_each_page,
      };
      allRows.push(item);
      if (item.repeat) repeatRows.push(item);
    });
  });

  const headerOf = () =>
    repeatRows.map(({ sectionIndex, rowIndex }) => ({ sectionIndex, rowIndex }));
  const repeatH = repeatRows.reduce((sum, r) => sum + r.h, 0);

  if (allRows.length === 0) {
    return [{ rows: [] }];
  }

  const pages: PrintPage[] = [];
  let cursor = 0;
  let firstPage = true;

  while (cursor < allRows.length) {
    const current: PrintPage = { rows: firstPage ? [] : headerOf() };
    let used = firstPage ? 0 : repeatH;
    let placed = 0;

    while (cursor < allRows.length) {
      const row = allRows[cursor];
      // Continuation pages already carry repeat bands in the header — skip them
      // when walking the document stream so they are not duplicated mid-page.
      if (!firstPage && row.repeat) {
        cursor += 1;
        continue;
      }
      const pageHasBody = placed > 0;
      if (used + row.h > contentH && pageHasBody) {
        break;
      }
      current.rows.push({ sectionIndex: row.sectionIndex, rowIndex: row.rowIndex });
      used += row.h;
      placed += 1;
      cursor += 1;
    }

    // Oversized first row on an empty page: force it alone (never split).
    if (placed === 0 && cursor < allRows.length) {
      const row = allRows[cursor];
      if (!firstPage && row.repeat) {
        cursor += 1;
        continue;
      }
      current.rows.push({ sectionIndex: row.sectionIndex, rowIndex: row.rowIndex });
      cursor += 1;
    }

    pages.push(current);
    firstPage = false;
  }

  return pages.length ? pages : [{ rows: [] }];
}
